import React, { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Mail,
  MessageSquare,
  Upload,
  Download,
  Send,
  CheckCircle,
  Clock,
  Users,
  FileText,
  AlertCircle,
  Zap,
} from "lucide-react";
import CreateCampaignForm from "@/components/CreateCampaignForm";
//import { CSVLink } from "react-csv";

// Sample guest data for export
const guestListData = [
  { name: "Rahul Sharma", email: "rahul@example.com", phone: "+919876543210", ticketType: "VIP", status: "Paid" },
  { name: "Priya Patel", email: "priya@example.com", phone: "+919876543211", ticketType: "Early Bird", status: "Paid" },
  { name: "Amit Kumar", email: "amit@example.com", phone: "+919876543212", ticketType: "Regular", status: "Pending" },
  { name: "Sneha Reddy", email: "sneha@example.com", phone: "+919876543213", ticketType: "Student", status: "Paid" },
];

// CSV Headers for export
const csvHeaders = [
  { label: "Name", key: "name" },
  { label: "Email", key: "email" },
  { label: "Phone", key: "phone" },
  { label: "Ticket Type", key: "ticketType" },
  { label: "Status", key: "status" },
];

// Email Templates
const emailTemplates = [
  {
    id: 1,
    name: "Registration Confirmation",
    type: "registration",
    subject: "Welcome to {{eventName}} - Registration Confirmed! 🎉",
    body: `Hi {{guestName}},

Thank you for registering for {{eventName}}!

Event Details:
📅 Date: {{eventDate}}
📍 Venue: {{eventVenue}}
🎟️ Ticket Type: {{ticketType}}
💰 Amount: ₹{{amount}}

Your booking ID: {{bookingId}}

See you there!

Best regards,
{{organizerName}}`,
    status: "active",
  },
  {
    id: 2,
    name: "Event Canceled",
    type: "canceled",
    subject: "Important: {{eventName}} has been Canceled",
    body: `Dear {{guestName}},

We regret to inform you that {{eventName}} scheduled for {{eventDate}} has been canceled.

Your refund of ₹{{amount}} will be processed within 5-7 business days.

Refund Details:
Booking ID: {{bookingId}}
Amount: ₹{{amount}}
Expected Date: {{refundDate}}

We apologize for any inconvenience.

Best regards,
{{organizerName}}`,
    status: "active",
  },
  {
    id: 3,
    name: "Event Postponed",
    type: "postponed",
    subject: "Update: {{eventName}} Date Changed",
    body: `Hi {{guestName}},

We want to inform you that {{eventName}} has been postponed.

New Event Details:
📅 New Date: {{newEventDate}}
📍 Venue: {{eventVenue}} (unchanged)
🎟️ Your Ticket: {{ticketType}}

Your existing ticket ({{bookingId}}) remains valid for the new date.

If you cannot attend the new date, please request a refund by {{refundDeadline}}.

Best regards,
{{organizerName}}`,
    status: "active",
  },
];

// Recent Campaigns
const recentCampaigns = [
  {
    id: 1,
    name: "Last Chance - 13 Tickets Left!",
    type: "email",
    date: "Nov 18, 6:00 PM",
    recipients: 1240,
    sent: 1240,
    opens: 612,
    clicks: 187,
    status: "sent",
  },
  {
    id: 2,
    name: "Early Bird Reminder",
    type: "sms",
    date: "Nov 10, 10:00 AM",
    recipients: 890,
    sent: 890,
    opens: 534,
    clicks: 234,
    status: "sent",
  },
];

export default function EmailCampaign() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importedGuests, setImportedGuests] = useState<any[]>([]);
  const [campaignType, setCampaignType] = useState("email");
  const [isCreateCampaignOpen, setIsCreateCampaignOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle CSV Import
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const rows = text.split("\n").map((row) => row.split(","));
        const headers = rows[0];
        const data = rows.slice(1).map((row) => {
          const obj: any = {};
          headers.forEach((header, index) => {
            obj[header.trim()] = row[index]?.trim();
          });
          return obj;
        });
        setImportedGuests(data);
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="p-6 space-y-6 bg-gray-950 min-h-screen">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white">Campaign Management</h1>
        <Dialog open={isCreateCampaignOpen} onOpenChange={setIsCreateCampaignOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
              <Send className="mr-2 h-4 w-4" />
              Create Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-3xl">
            <DialogHeader>
              <DialogTitle>Create New Campaign</DialogTitle>
              <DialogDescription className="text-gray-400">
                Design and send email or SMS campaigns to your guests
              </DialogDescription>
            </DialogHeader>
            <CreateCampaignForm setCampaignType={setCampaignType} />
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="campaigns" className="space-y-6">
        <TabsList className="bg-gray-900 border-gray-800">
          <TabsTrigger value="campaigns" className="data-[state=active]:bg-purple-600">
            <Mail className="mr-2 h-4 w-4" />
            Campaigns
          </TabsTrigger>
          <TabsTrigger value="guestlist" className="data-[state=active]:bg-purple-600">
            <Users className="mr-2 h-4 w-4" />
            Guest List
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-purple-600">
            <FileText className="mr-2 h-4 w-4" />
            Email Templates
          </TabsTrigger>
          <TabsTrigger value="automated" className="data-[state=active]:bg-purple-600">
            <Zap className="mr-2 h-4 w-4" />
            Automated Emails
          </TabsTrigger>
        </TabsList>

        {/* Recent Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-6">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Send className="mr-2 h-5 w-5 text-purple-500" />
                Recent Campaigns
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Campaign Name</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Type</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Date</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Recipients</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Opens</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Clicks</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentCampaigns.map((campaign) => (
                      <tr key={campaign.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                        <td className="py-3 px-4 text-white font-medium">{campaign.name}</td>
                        <td className="py-3 px-4">
                          <span className="flex items-center text-gray-300">
                            {campaign.type === "email" ? (
                              <Mail className="mr-2 h-4 w-4 text-purple-400" />
                            ) : (
                              <MessageSquare className="mr-2 h-4 w-4 text-pink-400" />
                            )}
                            {campaign.type.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-300">{campaign.date}</td>
                        <td className="py-3 px-4 text-gray-300">{campaign.recipients}</td>
                        <td className="py-3 px-4 text-gray-300">
                          {campaign.opens}{" "}
                          <span className="text-purple-400">
                            ({((campaign.opens / campaign.recipients) * 100).toFixed(1)}%)
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          {campaign.clicks}{" "}
                          <span className="text-pink-400">
                            ({((campaign.clicks / campaign.recipients) * 100).toFixed(1)}%)
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-900/30 text-green-400">
                            {campaign.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Guest List Import/Export Tab */}
        <TabsContent value="guestlist" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Import Section */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Upload className="mr-2 h-5 w-5 text-purple-500" />
                  Import Guest List
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    <Upload className="mx-auto h-12 w-12 text-gray-500 mb-4" />
                    <p className="text-white font-medium mb-2">Click to upload CSV file</p>
                    <p className="text-gray-400 text-sm">or drag and drop</p>
                    <p className="text-gray-500 text-xs mt-2">CSV files only</p>
                  </label>
                </div>
                {selectedFile && (
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <p className="text-white font-medium">Selected File:</p>
                    <p className="text-gray-400 text-sm">{selectedFile.name}</p>
                    <p className="text-green-400 text-sm mt-2">
                      ✓ {importedGuests.length} guests imported successfully
                    </p>
                  </div>
                )}
                <Button className="w-full bg-purple-600 hover:bg-purple-700">
                  <Upload className="mr-2 h-4 w-4" />
                  Import Guests
                </Button>
              </CardContent>
            </Card>

            {/* Export Section */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Download className="mr-2 h-5 w-5 text-pink-500" />
                  Export Guest List
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gray-800/50 rounded-lg p-6 text-center">
                  <Download className="mx-auto h-12 w-12 text-gray-500 mb-4" />
                  <p className="text-white font-medium mb-2">Download Guest List</p>
                  <p className="text-gray-400 text-sm mb-4">
                    Export {guestListData.length} guests to CSV format
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Total Guests:</span>
                      <span className="text-white font-medium">{guestListData.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">File Format:</span>
                      <span className="text-white font-medium">CSV</span>
                    </div>
                  </div>
                </div>
                {/* <CSVLink
                  data={guestListData}
                  headers={csvHeaders}
                  filename="guest-list-export.csv"
                  className="w-full"
                >
                  <Button className="w-full bg-pink-600 hover:bg-pink-700">
                    <Download className="mr-2 h-4 w-4" />
                    Export to CSV
                  </Button>
                </CSVLink> */}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Email Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <FileText className="mr-2 h-5 w-5 text-purple-500" />
                Email Templates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {emailTemplates.map((template) => (
                <div
                  key={template.id}
                  className="bg-gray-800/50 rounded-lg p-4 hover:bg-gray-800 transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-white font-medium">{template.name}</h3>
                      <p className="text-gray-400 text-sm mt-1">Type: {template.type}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-700 text-gray-300 hover:bg-gray-700"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-700 text-gray-300 hover:bg-gray-700"
                      >
                        Preview
                      </Button>
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded p-3 mt-3">
                    <p className="text-gray-400 text-xs mb-2">Subject:</p>
                    <p className="text-white text-sm">{template.subject}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automated Emails Tab */}
        <TabsContent value="automated" className="space-y-6">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Zap className="mr-2 h-5 w-5 text-yellow-500" />
                Automated Email Workflows
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                {
                  name: "Registration Confirmation",
                  trigger: "After successful payment",
                  sent: 487,
                  status: "active",
                  icon: CheckCircle,
                },
                {
                  name: "Payment Receipt",
                  trigger: "Immediately after payment",
                  sent: 487,
                  status: "active",
                  icon: CheckCircle,
                },
                {
                  name: "Event Reminder (1 week)",
                  trigger: "7 days before event",
                  sent: 0,
                  status: "scheduled",
                  icon: Clock,
                },
                {
                  name: "Event Reminder (1 day)",
                  trigger: "1 day before event",
                  sent: 0,
                  status: "scheduled",
                  icon: Clock,
                },
                {
                  name: "Event Canceled Notification",
                  trigger: "When event is canceled",
                  sent: 0,
                  status: "standby",
                  icon: AlertCircle,
                },
                {
                  name: "Event Postponed Notification",
                  trigger: "When event is postponed",
                  sent: 0,
                  status: "standby",
                  icon: AlertCircle,
                },
              ].map((workflow, idx) => {
                const Icon = workflow.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-4 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <Icon
                        className={`h-5 w-5 ${
                          workflow.status === "active"
                            ? "text-green-500"
                            : workflow.status === "scheduled"
                              ? "text-yellow-500"
                              : "text-gray-500"
                        }`}
                      />
                      <div>
                        <p className="text-white font-medium">{workflow.name}</p>
                        <p className="text-sm text-gray-400">Trigger: {workflow.trigger}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {workflow.sent > 0 ? `${workflow.sent} emails sent` : "No emails sent yet"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          workflow.status === "active"
                            ? "bg-green-900/30 text-green-400"
                            : workflow.status === "scheduled"
                              ? "bg-yellow-900/30 text-yellow-400"
                              : "bg-gray-700/30 text-gray-400"
                        }`}
                      >
                        {workflow.status.charAt(0).toUpperCase() + workflow.status.slice(1)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-400 hover:text-white hover:bg-gray-700"
                      >
                        Configure
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}


